import { NavLink, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  Clock, 
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Building2,
  Receipt
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useCompany } from '@/hooks/useCompany';
import { useCurrentUser } from '@/hooks/useCurrentUser';

const navigationGroups = [
  {
    title: 'Dados',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    ]
  },
  {
    title: 'Cobrança',
    items: [
      { to: '/regua', icon: Clock, label: 'Réguas' },
      { to: '/clientes', icon: Users, label: 'Clientes' },
      { to: '/faturas', icon: Receipt, label: 'Faturas' },
    ]
  },
  {
    title: 'Sistema',
    items: [
      { to: '/configuracoes', icon: Settings, label: 'Configurações' },
      { to: '/usuarios', icon: Users, label: 'Usuários' },
    ]
  }
];

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const { data: company } = useCompany();
  const { data: currentUser } = useCurrentUser();

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  const isActive = (path: string) => {
    return location.pathname === path || 
           (path !== '/dashboard' && location.pathname.startsWith(path));
  };

  return (
    <aside
      className={cn(
        "relative flex flex-col border-r bg-[hsl(var(--basic-50))] transition-all duration-300 h-screen sticky top-0",
        collapsed ? "w-16" : "w-64"
      )}
    >
      {/* Logo & Company */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-[hsl(var(--basic-100))] bg-card">
        {!collapsed && (
          <div className="flex items-center gap-3 min-w-0">
            {company?.logo_url ? (
              <img 
                src={company.logo_url} 
                alt={company.trade_name || company.name} 
                className="h-9 w-9 object-contain rounded-lg"
              />
            ) : (
              <div className="h-9 w-9 rounded-lg bg-[hsl(var(--basic-100))] flex items-center justify-center">
                <Building2 className="h-5 w-5 text-[hsl(var(--basic-500))]" />
              </div>
            )}
            <span className="text-sm font-bold text-[hsl(var(--basic-900))] truncate">
              {company?.trade_name || company?.name || 'Empresa'}
            </span>
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setCollapsed(!collapsed)}
          className="ml-auto shrink-0"
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-8 overflow-y-auto">
        {navigationGroups.map((group) => (
          <div key={group.title}>
            {!collapsed && (
              <h3 className="px-4 text-[10px] font-bold text-[hsl(var(--basic-400))] uppercase tracking-widest mb-2">
                {group.title}
              </h3>
            )}
            <ul className="space-y-1">
              {group.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    className={cn(
                      "flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 hover-pop cursor-pointer",
                      isActive(item.to)
                        ? "bg-[hsl(var(--basic-500))] text-white shadow-md"
                        : "text-[hsl(var(--basic-600))] hover:bg-card hover:text-[hsl(var(--basic-800))]"
                    )}
                  >
                    <item.icon className="h-5 w-5 shrink-0" />
                    {!collapsed && <span>{item.label}</span>}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* User Section */}
      <div className="p-4">
        <div className="p-2 rounded-xl bg-card border border-[hsl(var(--basic-100))]">
          <div className="flex items-center gap-3 p-2 rounded-lg">
            <div className="size-8 rounded-full bg-[hsl(var(--basic-100))] flex items-center justify-center">
              <Users className="size-4 text-[hsl(var(--basic-700))]" />
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-[hsl(var(--basic-900))] truncate">
                  {currentUser?.full_name || currentUser?.email || 'Usuário'}
                </p>
              </div>
            )}
          </div>
          
          <button 
            onClick={handleLogout}
            className={cn(
              "mt-3 text-sm px-3 text-muted-foreground hover:text-destructive hover:underline",
              "rounded-lg hover:bg-[hsl(var(--careful-50))] transition-colors font-medium",
              "cursor-pointer w-full text-left flex items-center gap-2",
              collapsed && "justify-center"
            )}
          >
            <LogOut className="size-4" />
            {!collapsed && <span>Sair do sistema</span>}
          </button>
        </div>
      </div>
    </aside>
  );
}
